import React, { useMemo } from "react";
import { CareRecipient } from "../../../../../domain/care_recipient/CareRecipient";
import { CareRecipientWithDataSyncInformation } from "../../../../../domain/care_recipient/CareRecipientWithDataSyncInformation";
import { getLabelFromUserAccountRole } from "../../../../../domain/user/account/UserAccountRole";
import { User } from "../../../../../domain/user/User";
import { EditAndDeleteButton } from "./EditAndDeleteButton";

export const AdminListTableRow = React.memo(function AdminListTableRow(props: Props) {
    const {
        careRecipientWithDataSyncInfo,
        users,
        handleCareRecipientDeleteClick,
        handleCareRecipientEditClick,
        handleUserDeleteClick,
        handleUserEditClick
    } = props;

    const [firstUser, ...rest] = users;

    return (
        <>
            <tr>
                <CareRecipientData
                    careRecipientWithDataSyncInfo={careRecipientWithDataSyncInfo}
                    handleCareRecipientDeleteClick={handleCareRecipientDeleteClick}
                    handleCareRecipientEditClick={handleCareRecipientEditClick}
                    users={users}
                />
                <UserData
                    handleUserDeleteClick={handleUserDeleteClick}
                    handleUserEditClick={handleUserEditClick}
                    user={firstUser}
                />
            </tr>
            {rest.length > 0 && rest.map(user => (
                <tr key={user.id.value}>
                    <UserData
                        handleUserDeleteClick={handleUserDeleteClick}
                        handleUserEditClick={handleUserEditClick}
                        user={user}
                    />
                </tr>
            ))}
        </>
    );
});

const CareRecipientData = React.memo(function CareRecipientData(props: CareRecipientDataProps) {
    const {
        careRecipientWithDataSyncInfo,
        handleCareRecipientEditClick,
        handleCareRecipientDeleteClick,
        users,
    } = props;

    const careRecipient = useMemo(() => careRecipientWithDataSyncInfo?.careRecipient, [careRecipientWithDataSyncInfo]);

    const startTime = useMemo(() => careRecipientWithDataSyncInfo?.dataSyncStartTime, [careRecipientWithDataSyncInfo]);

    const endTime = useMemo(() => careRecipientWithDataSyncInfo?.dataSyncEndTime, [careRecipientWithDataSyncInfo]);

    const houseId = useMemo(() => careRecipient?.houseId, [careRecipient]);

    const rowSpan = useMemo(() => users.length ? users.length : 1, [users]);

    return (
        <>
            <td rowSpan={rowSpan}>{houseId?.value ?? "-"}</td>
            <td rowSpan={rowSpan}>{careRecipient?.name.value ?? "-"}</td>
            <td rowSpan={rowSpan}>{careRecipient?.birthday?.format("/") ?? "-"}</td>
            <td rowSpan={rowSpan}>{startTime?.format() ?? "-"}</td>
            <td rowSpan={rowSpan}>{endTime?.format() ?? "-"}</td>
            {careRecipient?.id ? (
                <td rowSpan={rowSpan}>
                    <EditAndDeleteButton
                        onEditClick={handleCareRecipientEditClick(careRecipient)}
                        onDeleteClick={handleCareRecipientDeleteClick(careRecipient)}
                    />
                </td>
            ) : (
                <td rowSpan={users.length ? users.length : 1}>{"-"}</td>
            )}
        </>
    );
});

const UserData = React.memo(function UserData(props: { user: User | null } & UserDataProps) {
    const {
        user,
        handleUserDeleteClick,
        handleUserEditClick,
    } = props;

    return user ? (
        <>
            <td>{user.name.value}</td>
            <td>{user.email.value}</td>
            <td>{getLabelFromUserAccountRole(user.role)}</td>
            <td className="button_td">
                <EditAndDeleteButton
                    onEditClick={handleUserEditClick(user)}
                    onDeleteClick={handleUserDeleteClick(user)}
                />
            </td>
        </>
    ) : (
        <>
            <td>{"-"}</td>
            <td>{"-"}</td>
            <td>{"-"}</td>
            <td>{"-"}</td>
        </>
    );
});

type CareRecipientDataProps = {
    careRecipientWithDataSyncInfo: CareRecipientWithDataSyncInformation | null
    handleCareRecipientEditClick: (careRecipient: CareRecipient) => () => void
    handleCareRecipientDeleteClick: (careRecipient: CareRecipient) => () => void
    users: User[]   
};

type UserDataProps = {
    handleUserDeleteClick: (user: User) => () => void
    handleUserEditClick: (user: User) => () => void
};

type Props = CareRecipientDataProps & UserDataProps;
